import React, { useRef } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

/**
 * Cross-org guard via localStorage events.
 *
 * We store the org-code that was active at the time of authentication in a
 * special localStorage key so that ALL tabs/windows AND same-tab URL changes
 * can detect when a user navigates to a different org URL and force re-login.
 *
 * The key trick: we write to localStorage on every render where the user is
 * authenticated and the target orgCode is known.  But we only *write* once
 * (the first time a valid orgCode is seen after auth), and we *compare*
 * the current URL's orgCode against that saved value.
 */
const LOGIN_ORG_KEY = '_loginOrg';

function getLoginOrg() {
  return localStorage.getItem(LOGIN_ORG_KEY);
}

function setLoginOrg(code) {
  if (code) {
    localStorage.setItem(LOGIN_ORG_KEY, code.toUpperCase());
  }
}

function clearLoginOrg() {
  localStorage.removeItem(LOGIN_ORG_KEY);
}

const ProtectedRoute = ({ children, requiredRole, requiredFeature, requireAdmin, orgFeature }) => {
  const { t } = useTranslation();
  const { isAuthenticated, loading, isAdmin, hasFeature, user, orgFeatureEnabled } = useAuth();
  const location = useLocation();
  const { orgCode: routeOrgCode } = useParams();
  const orgCode = routeOrgCode || localStorage.getItem('orgCode');
  const orgPrefix = orgCode ? `/${orgCode.toLowerCase()}` : '';
  const loginPath = `${orgPrefix}/login`;
  const homePath = `${orgPrefix}/`;
  const guardTriggered = useRef(false);

  // ── Capture the current orgCode as the "login org" if not already set.
  //    Must happen synchronously during render, not in a useEffect — an
  //    effect runs one render after the cross-org guard below, so on the
  //    very first render right after a legitimate login it would still see
  //    no saved login org, mistake the fresh login for a suspicious cross-org
  //    URL change, and force an immediate re-login loop. ──
  if (isAuthenticated && orgCode && !getLoginOrg()) {
    setLoginOrg(orgCode);
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={loginPath} replace />;
  }

  // ── Cross-org guard ──────────────────────────────────────────────
  // 1. If the backend told us the user's org (user.org_code), check it
  //    matches the URL org. Staff/superusers are exempt.
  if (user?.org_code && orgCode && !user.is_staff) {
    if (user.org_code.toUpperCase() !== orgCode.toUpperCase()) {
      const correctOrg = user.org_code.toLowerCase();
      clearLoginOrg();
      return <Navigate to={`/${correctOrg}/`} replace />;
    }
  }

  // 2. Fallback: detect URL org change via the saved login org.
  //    This catches ALL cases: null user.org_code, admin users, etc.
  //    Even is_staff users trigger this — if you logged in at /rta/ and
  //    change to /pta/, you need to re-authenticate for PTA.
  const loginOrg = getLoginOrg();
  if (orgCode) {
    // Case A: We have a saved login org AND it doesn't match the URL org → re-login
    if (loginOrg && loginOrg.toUpperCase() !== orgCode.toUpperCase()) {
      if (!guardTriggered.current) {
        guardTriggered.current = true;
        localStorage.removeItem('token');
        localStorage.removeItem('tokenExpiresAt');
        clearLoginOrg();
        window.location.href = loginPath;
      }
      return null;
    }
    // Case B: No login org saved yet, but we're at an org-prefixed URL
    // while already authenticated.  This happens when you logged in at
    // /login (no org prefix) and then manually changed the URL to /pta/.
    // We must force re-login because we don't know if you belong here.
    if (!loginOrg) {
      if (!guardTriggered.current) {
        guardTriggered.current = true;
        localStorage.removeItem('token');
        localStorage.removeItem('tokenExpiresAt');
        clearLoginOrg();
        window.location.href = loginPath;
      }
      return null;
    }
  }

  // Admin-only gate
  if (requireAdmin && !isAdmin) {
    return <Navigate to={homePath} replace />;
  }

  // Role gate — admin always bypasses role restrictions
  if (requiredRole) {
    const userRole = (user?.role?.name || user?.role || '').toLowerCase();
    if (userRole !== requiredRole.toLowerCase() && !isAdmin) {
      return <Navigate to={homePath} replace />;
    }
  }

  // Feature gate — check role's assigned features; admin always bypasses.
  // If the blocked page IS homePath (dashboard), redirect to /profile instead
  // to avoid an infinite redirect loop.
  if (requiredFeature && !hasFeature(requiredFeature) && !isAdmin) {
    const fallback = location.pathname === homePath
      ? `${orgPrefix}/profile`
      : homePath;
    return <Navigate to={fallback} replace />;
  }

  // Org-level feature gate — bypass if user's role explicitly has the feature
  if (orgFeature && !orgFeatureEnabled(orgFeature) && !hasFeature(orgFeature) && !isAdmin) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', flexDirection: 'column', gap: '12px', color: '#888' }}>
        <span style={{ fontSize: '56px' }}>🔒</span>
        <h2 style={{ margin: 0, color: '#555' }}>{t('protectedRoute.featureNotAvailable')}</h2>
        <p style={{ margin: 0, fontSize: '14px' }}>{t('protectedRoute.featureDisabledNote')}</p>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
